/**
 * "What changed since I last looked" — the Check changes dialog, first written for the Master Sheet
 * (2026-10-09) and shared with the SAP Ship Doc since 2026-10-14 (Max: "check changes giống bên
 * mastersheet"). ONE dialog: what differs between the two screens is `cfg` — the noun, the columns
 * that identify a row, the filter, the search — so "Changed", "New row" and "Mark as checked" cannot
 * come to mean different things on the two screens.
 *
 * Max, for Tram: the sheet is filled from SAP and NetSuite, other teams change those systems, and
 * nothing on the sheet said that a figure had moved. Pressing Check reads the whole sheet on the
 * server, compares it with the last time THIS person marked it as checked, and lists every new row,
 * every row gone and every cell that moved, old value beside new. The server keeps the snapshots;
 * see masterSheetChanges.js for why a check is pending until it is marked.
 *
 * The values are printed through the screen's own formatter, so a changed price reads exactly the way
 * the cell beside it in the table does.
 *
 * cfg: { noun, reading, groupField, groupAll, searchKeys, searchPlaceholder, columns: [{ label, cell(x),
 *        cls }], size(x, fmt), fileName }
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { X, FileDown, CheckCheck, RefreshCw } from 'lucide-react';
import { useTranslation, Trans } from 'react-i18next';
import i18n from './i18n';
import { fmtDateTimeVN } from './timeZone';

const PILL = { changed: 'idle', added: 'ok', removed: 'err' };
const PAGE = 200;

export default function ChangeCheckDialog({ base, fmt, cfg, onClose }) {
  const { t } = useTranslation();
  // The word on a row's pill, by the server's change kind (the kind itself stays the key).
  const WORD = { changed: t('xChange.wordChanged'), added: t('xChange.wordAdded'), removed: t('xChange.wordRemoved') };
  const rowsWord = (n) => t('xChange.rows', { count: Number(n || 0), n: Number(n || 0).toLocaleString('en-GB') });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [marking, setMarking] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [done, setDone] = useState('');
  const [kind, setKind] = useState('');
  const [factory, setFactory] = useState('');
  const [column, setColumn] = useState('');
  const [search, setSearch] = useState('');
  const [shown, setShown] = useState(PAGE);
  // An earlier starting point the person picked, or null for their last check.
  const [since, setSince] = useState(null);

  const run = useCallback(async (since) => {
    setLoading(true);
    setError('');
    setDone('');
    try {
      const response = await fetch(`${base}/check`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(since ? { since } : {}),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || i18n.t('xChange.checkFailed', { status: response.status }));
      setData(body);
      setKind(''); setFactory(''); setColumn(''); setSearch(''); setShown(PAGE);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [base]);

  // Once, on opening. A pick from "Compare with" runs it again itself, so marking a check (which
  // resets the pick) does not set off a second five-second read behind the person's back.
  useEffect(() => { run(null); }, [run]);

  const markChecked = async () => {
    if (!data?.current?.id) return;
    setMarking(true);
    setError('');
    try {
      const response = await fetch(`${base}/review`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: data.current.id }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || t('xChange.saveFailed', { status: response.status }));
      setDone(t('xChange.savedNext', { noun: cfg.noun, at: fmtDateTimeVN(data.current.createdAt) }));
      setData((d) => ({ ...d, marked: true }));
      setSince(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setMarking(false);
    }
  };

  const exportList = async () => {
    setExporting(true);
    setError('');
    try {
      const response = await fetch(`${base}/export-xlsx`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ currentId: data.current.id, baselineId: data.baseline.id }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || t('xChange.exportFailed', { status: response.status }));
      }
      const blob = await response.blob();
      const name = (response.headers.get('Content-Disposition') || '').match(/filename="([^"]+)"/)?.[1]
        || cfg.fileName;
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url; link.download = name;
      document.body.appendChild(link); link.click(); link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setExporting(false);
    }
  };

  const labels = data?.labels || {};
  const label = (c) => labels[c] || c.replace(/_/g, ' ');
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data?.changes || []).filter((x) => (!kind || x.t === kind)
      && (!factory || (x.id[cfg.groupField] || '(none)') === factory)
      && (!column || x.f.some((g) => g.c === column))
      && (!q || [...cfg.searchKeys.map((k) => x.id[k]), x.k]
        .some((v) => String(v || '').toLowerCase().includes(q))));
  }, [data, kind, factory, column, search, cfg]);

  const s = data?.summary;
  // byGroup since the dialog was shared; byFactory is what a Master Sheet server from before sends.
  const byGroup = s ? (s.byGroup || s.byFactory || {}) : {};
  const columnsByCount = s ? Object.entries(s.byColumn).sort((a, b) => b[1] - a[1]) : [];

  return (
    <div className="sp-modal-back sp-chg-back" onClick={onClose}>
      <div className="sp-modal sp-changesbox" role="dialog" aria-label={t('xChange.title')}
        onClick={(e) => e.stopPropagation()}>
        <div className="sp-modal-h">
          <div>
            <strong>{t('xChange.title')}</strong>
            <small>
              {data?.baseline
                ? <Trans t={t} i18nKey="xChange.comparing" components={{ b: <b /> }} values={{
                  noun: cfg.noun,
                  now: (data.current.rows || 0).toLocaleString('en-GB'),
                  at: fmtDateTimeVN(data.baseline.reviewedAt),
                  then: (data.baseline.rows || 0).toLocaleString('en-GB'),
                }} />
                : t('xChange.intro')}
            </small>
          </div>
          <button type="button" className="sp-icon-btn" aria-label={t('xChange.close')} onClick={onClose}><X size={15} /></button>
        </div>

        <div className="sp-modal-body">
          {error && <div className="sp-alert err">{error}</div>}
          {done && <div className="sp-alert info sp-chg-done">{done}</div>}
          {loading && <div className="sp-empty">{cfg.reading}</div>}

          {!loading && data && !data.baseline && (
            <div className="sp-empty-state">
              <h3>{t('xChange.firstTitle')}</h3>
              <p>
                {t('xChange.firstBody', { noun: cfg.noun, n: (data.current.rows || 0).toLocaleString('en-GB') })}
              </p>
            </div>
          )}

          {!loading && data?.baseline && s && (
            <>
              <div className="sp-chg-summary">
                <button type="button" className={`sp-chg-chip${kind === 'changed' ? ' on' : ''}`}
                  onClick={() => { setKind(kind === 'changed' ? '' : 'changed'); setShown(PAGE); }}>
                  <span className="sp-pill idle">{t('xChange.chipChanged')}</span> {rowsWord(s.changed)}
                </button>
                <button type="button" className={`sp-chg-chip${kind === 'added' ? ' on' : ''}`}
                  onClick={() => { setKind(kind === 'added' ? '' : 'added'); setShown(PAGE); }}>
                  <span className="sp-pill ok">{t('xChange.chipNew')}</span> {rowsWord(s.added)}
                </button>
                <button type="button" className={`sp-chg-chip${kind === 'removed' ? ' on' : ''}`}
                  onClick={() => { setKind(kind === 'removed' ? '' : 'removed'); setShown(PAGE); }}>
                  <span className="sp-pill err">{t('xChange.chipGone')}</span> {rowsWord(s.removed)}
                </button>
              </div>

              {(s.changed + s.added + s.removed) === 0 ? (
                <div className="sp-alert info">{t('xChange.nothingChanged', { noun: cfg.noun, at: fmtDateTimeVN(data.baseline.reviewedAt) })}</div>
              ) : (
                <>
                  {columnsByCount.length > 0 && (
                    <div className="sp-chg-cols">
                      <span>{t('xChange.cellsByColumn')}</span>
                      {columnsByCount.map(([c, n]) => (
                        <button key={c} type="button" className={`sp-chg-col${column === c ? ' on' : ''}`}
                          onClick={() => { setColumn(column === c ? '' : c); setShown(PAGE); }}>
                          {label(c)} <b>{n.toLocaleString('en-GB')}</b>
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="sp-chg-filters">
                    <select value={factory} onChange={(e) => { setFactory(e.target.value); setShown(PAGE); }}
                      aria-label={cfg.groupAll}>
                      <option value="">{cfg.groupAll}</option>
                      {Object.entries(byGroup).sort((a, b) => b[1] - a[1]).map(([f, n]) => (
                        <option key={f} value={f}>{f} ({n})</option>
                      ))}
                    </select>
                    <input type="search" value={search} placeholder={cfg.searchPlaceholder}
                      onChange={(e) => { setSearch(e.target.value); setShown(PAGE); }} aria-label={t('xChange.searchAria')} />
                    {(kind || factory || column || search) && (
                      <button type="button" className="sp-btn" onClick={() => {
                        setKind(''); setFactory(''); setColumn(''); setSearch(''); setShown(PAGE);
                      }}>{t('xChange.clear')}</button>
                    )}
                    <span className="sp-chg-count">{t('xChange.countOf', { shown: rows.length.toLocaleString('en-GB'), total: (data.total || 0).toLocaleString('en-GB') })}</span>
                  </div>
                  {data.truncated && (
                    <div className="sp-alert info">
                      {t('xChange.truncated', {
                        shown: (data.changes || []).length.toLocaleString('en-GB'),
                        total: data.total.toLocaleString('en-GB'),
                      })}
                    </div>
                  )}
                  <div className="sp-table-wrap sp-chg-wrap">
                    <table className="sp-table sp-chg-table">
                      <thead>
                        <tr>
                          <th>{t('xChange.colChange')}</th>
                          {cfg.columns.map((c) => <th key={c.label}>{c.label}</th>)}
                          <th>{t('xChange.colWhat')}</th><th>{t('xChange.colWhere')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.slice(0, shown).map((x) => (
                          <tr key={`${x.t}-${x.k}`}>
                            <td><span className={`sp-pill ${PILL[x.t]}`}>{WORD[x.t]}</span></td>
                            {cfg.columns.map((c) => <td key={c.label} className={c.cls || undefined}>{c.cell(x) || '—'}</td>)}
                            <td className="sp-chg-what">
                              {x.t === 'changed' ? x.f.map((g) => (
                                <div key={g.c} className={column && g.c !== column ? 'dim' : ''}>
                                  <span className="col">{label(g.c)}</span>{' '}
                                  <span className="old">{fmt(g.c, g.o)}</span> → <span className="new">{fmt(g.c, g.n)}</span>
                                </div>
                              )) : (
                                <div>{cfg.size(x, fmt)}</div>
                              )}
                            </td>
                            <td className="sp-chg-via">
                              {x.t === 'changed'
                                ? [...new Set(x.f.map((g) => g.via))].map((v) => <div key={v}>{v}</div>)
                                : x.via}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {rows.length > shown && (
                    <div className="sp-chg-more">
                      <button type="button" className="sp-btn" onClick={() => setShown((n) => n + PAGE)}>
                        {t('xChange.showMore', { n: Math.min(PAGE, rows.length - shown) })}
                      </button>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>

        <div className="sp-modal-f">
          {data?.checkpoints?.length > 1 && (
            <label className="sp-chg-since">
              {t('xChange.compareWith')}
              <select value={data.baseline?.id || ''} disabled={loading}
                onChange={(e) => { const v = Number(e.target.value) || null; setSince(v); run(v); }}>
                {data.checkpoints.map((c, i) => (
                  <option key={c.id} value={c.id}>
                    {t(i === 0 ? 'xChange.myLastCheck' : 'xChange.earlierCheck', { at: fmtDateTimeVN(c.reviewedAt) })}
                  </option>
                ))}
              </select>
            </label>
          )}
          <button type="button" className="sp-btn" disabled={loading} onClick={() => run(since)}>
            <RefreshCw size={14} /> {t('xChange.checkAgain')}
          </button>
          {data?.baseline && (data.total || 0) > 0 && (
            <button type="button" className="sp-btn" disabled={loading || exporting} onClick={exportList}>
              <FileDown size={14} /> {exporting ? t('xChange.building') : t('xChange.exportExcel')}
            </button>
          )}
          <button type="button" className="sp-btn" onClick={onClose}>{t('xChange.close')}</button>
          <button type="button" className="sp-btn primary" disabled={loading || marking || !data?.current || data?.marked}
            title={t('xChange.markHint')}
            onClick={markChecked}>
            <CheckCheck size={14} /> {data?.baseline ? (data?.marked ? t('xChange.marked') : t('xChange.mark'))
              : (data?.marked ? t('xChange.savedShort') : t('xChange.saveStart'))}
          </button>
        </div>
      </div>
    </div>
  );
}
